import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useSnackbar } from "notistack";
import {
  Autocomplete,
  Box,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import Holder from "../../Components/HOC/Holder";
import CustomBtn from "../../Components/Reusable/CustomBtn";
import TableReusable from "../../Components/Reusable/TableReusable";
import DesBtn from "../../Components/Reusable/DesBtn";
import WorkspaceTabs from "../../Components/Workspace/WorkspaceTabs";
import { errorDetail } from "./WorkspaceMembers";
import { activeWorkspaceState, authState, featuresState, popupState } from "../../Recoil/RecoilState";
import {
  baseURL,
  cameraModelTypesArr,
  cameraModelsAdminColumns,
  streamTypesArr,
} from "../../utils/StaticVariables";

const detectionModelArrayTypesArr = cameraModelTypesArr.filter((m) =>
  ["fire_smoke", "shoplifting"].includes(m.value)
);

const booleanFlagFields = [
  { field: "is_blocked_exit_camera", label: "Blocked Exit", feature: "blocked_exit" },
  { field: "is_no_entry_zone_camera", label: "No Entry Zone", feature: "no_entry_zone" },
  { field: "is_people_counting_camera", label: "People Counting", feature: "people_counting" },
  { field: "is_lost_found_camera", label: "Lost & Found", feature: "lost_found" },
];

// Add/Edit Camera Form for Workspace Admin (strictly scoped to the active workspace)
const WorkspaceCameraForm = ({ camera, members = [], enabledFeatures = [], onSubmit }) => {
  const isEdit = Boolean(camera);
  const [details, setDetails] = useState({
    name: camera?.name || "",
    path: camera?.path || "",
    type: camera?.type || "rtsp",
  });
  const [ownerId, setOwnerId] = useState(camera?.user_id || "");
  const [selected, setSelected] = useState(camera?.detection_models || []);
  const [flags, setFlags] = useState(
    booleanFlagFields.reduce(
      (acc, { field }) => ({ ...acc, [field]: !!camera?.[field] }),
      {}
    )
  );

  const openFeatures = new Set(enabledFeatures || []);
  const isClosed = (feature) => openFeatures.size > 0 && !openFeatures.has(feature);

  const setDetail = (e) => setDetails((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const canSave =
    details.name.trim() && details.path.trim() && (isEdit || ownerId);

  return (
    <Box>
      <Stack spacing={2} mb={2}>
        {!isEdit && (
          <TextField
            select
            label="Owner"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            helperText={!members.length ? "No members found in this workspace." : " "}
            fullWidth
          >
            {members.map((m) => (
              <MenuItem key={m.user_id || m.id} value={m.user_id || m.id}>
                {m.username}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField label="Name" name="name" value={details.name} onChange={setDetail} inputProps={{ maxLength: 50 }} fullWidth />
        <TextField label="Path / URL" name="path" value={details.path} onChange={setDetail} inputProps={{ maxLength: 255 }} fullWidth />
        <TextField select label="Type" name="type" value={details.type} onChange={setDetail} fullWidth>
          {[...new Set([...streamTypesArr, "video file", details.type])].map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Autocomplete
        multiple
        options={detectionModelArrayTypesArr}
        getOptionDisabled={(option) => isClosed(option.value)}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={detectionModelArrayTypesArr.filter((m) => selected.includes(m.value))}
        onChange={(e, newValue) => setSelected(newValue.map((v) => v.value))}
        renderInput={(params) => (
          <TextField {...params} label="Detection Models" placeholder="Fire & Smoke / Shoplifting" />
        )}
      />

      <Typography mt={2} mb={0.5} variant="body2" fontWeight={600} color="text.secondary">
        Other Detection Features
      </Typography>
      <Stack>
        {booleanFlagFields.map(({ field, label, feature }) => (
          <FormControlLabel
            key={field}
            disabled={isClosed(feature) && !flags[field]}
            control={
              <Switch
                checked={flags[field]}
                onChange={(e) =>
                  setFlags((prev) => ({ ...prev, [field]: e.target.checked }))
                }
              />
            }
            label={isClosed(feature) ? `${label} (closed for this workspace)` : label}
          />
        ))}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" mt={2}>
        <CustomBtn
          disable={!canSave}
          handle={() =>
            onSubmit({
              ...details,
              detection_models: selected,
              ...flags,
              ...(isEdit ? {} : { user_id: ownerId }),
            })
          }
        >
          {isEdit ? "Save" : "Add Camera"}
        </CustomBtn>
      </Stack>
    </Box>
  );
};

const WorkspaceCameras = () => {
  const { token } = useRecoilValue(authState);
  const activeWorkspace = useRecoilValue(activeWorkspaceState);
  const workspaceFeatures = useRecoilValue(featuresState);
  const { enqueueSnackbar } = useSnackbar();
  const setPopup = useSetRecoilState(popupState);
  const [cameras, setCameras] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const workspaceId = activeWorkspace?.workspace_id;
  const enabledFeatures = activeWorkspace?.enabled_features || workspaceFeatures || [];

  const fetchMembers = useCallback(async () => {
    if (!workspaceId || !token) return [];
    try {
      const { data } = await axios.get(`${baseURL}workspaces/${workspaceId}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(data || []);
      return data || [];
    } catch (error) {
      return [];
    }
  }, [workspaceId, token]);

  const fetchCameras = useCallback(async () => {
    if (!workspaceId || !token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${baseURL}source/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { workspaceId },
      });
      setCameras(data || []);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.detail || "Failed to load cameras",
        { variant: "error" }
      );
    } finally {

      setLoading(false);
    }
  }, [workspaceId, token, enqueueSnackbar]);

  useEffect(() => {
    fetchCameras();
    fetchMembers();
  }, [fetchCameras, fetchMembers]);

  const handleCreate = async (values) => {
    try {
      await axios.post(
        `${baseURL}admin/source`,
        { ...values, workspace_id: workspaceId },
        auth
      );
      enqueueSnackbar(`Camera ${values.name} added successfully`, { variant: "success" });
      setPopup((p) => ({ ...p, isOpen: false }));
      fetchCameras();
    } catch (error) {
      enqueueSnackbar(errorDetail(error, "Failed to add camera"), { variant: "error" });
    }
  };

  const handleSave = async (camera, values) => {
    try {
      await axios.put(`${baseURL}source`, [{ id: camera.id, ...values }], auth);
      enqueueSnackbar("Camera updated successfully", { variant: "success" });
      setPopup((p) => ({ ...p, isOpen: false }));
      fetchCameras();
    } catch (error) {
      const failed = error?.response?.data?.failed_ids?.[0];
      enqueueSnackbar(failed || errorDetail(error, "Failed to update camera"), {
        variant: "error",
      });
    }
  };

  const handleDelete = async (camera) => {
    try {
      const { data } = await axios.delete(`${baseURL}source`, { ...auth, data: { ids: [camera.id] } });
      if (!data?.deleted_ids?.length) throw new Error("not deleted");
      enqueueSnackbar(`Camera ${camera.name} deleted successfully`, { variant: "success" });
      fetchCameras();
    } catch (error) {
      enqueueSnackbar(errorDetail(error, "Failed to delete camera"), { variant: "error" });
    }
  };

  const openCreatePopup = () => {
    setPopup({
      isOpen: true,
      title: "Add Camera to Workspace",
      content: (
        <WorkspaceCameraForm
          members={members}
          enabledFeatures={enabledFeatures}
          onSubmit={handleCreate}
        />
      ),
      sendReq: null,
    });
  };

  const openEditPopup = (camera) => {
    setPopup({
      isOpen: true,
      title: "Edit Camera",
      content: (
        <WorkspaceCameraForm
          camera={camera}
          enabledFeatures={enabledFeatures}
          onSubmit={(values) => handleSave(camera, values)}
        />
      ),
      sendReq: null,
    });
  };

  const openDeletePopup = (camera) => {
    setPopup({
      isOpen: true,
      title: "Delete Camera",
      content: `Delete ${camera.name} from this workspace? This cannot be undone.`,
      sendReq: () => handleDelete(camera),
    });
  };

  const tableData = cameras.map((cam) => ({
    ...cam,
    models:
      [
        ...new Set(
          [
            ...(cam.detection_models || [])
              .map((v) => cameraModelTypesArr.find((m) => m.value === v)?.label || v),
            ...booleanFlagFields.filter(({ field }) => cam[field]).map(({ label }) => label),
          ].filter(Boolean)
        ),
      ].join(", ") || "—",
    actions: (
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="center">
        <DesBtn text="Edit camera details and models" handle={() => openEditPopup(cam)}>
          Edit
        </DesBtn>
        <DesBtn text="Delete camera" handle={() => openDeletePopup(cam)}>
          Delete
        </DesBtn>
      </Stack>
    ),
  }));

  return (
    <>
      <WorkspaceTabs />
      <Holder
        title={`Workspace Cameras — ${activeWorkspace?.name || ""}`}
        action={
          <CustomBtn disable={!workspaceId} handle={openCreatePopup}>
            Add Camera
          </CustomBtn>
        }
      >
        <Box my={2} sx={{ width: "100%", overflowX: "auto" }}>
          <TableReusable data={tableData} columns={cameraModelsAdminColumns} loading={loading} />
        </Box>
      </Holder>
    </>
  );
};


export default WorkspaceCameras;
